import {FC, ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {useQuery} from 'react-query'
import clsx from 'clsx'
import {useAuth} from '../../modules/auth'
import {isOnPremisesInstallation} from '../../modules/auth/core/onPremisesAccess'
import {
  getReportRuns,
  getReportSettings,
  lastRunSmsTitle,
  ReportRunRow,
  ReportSchedule,
  reportKeys,
  triggerTitle,
} from '../../modules/business-services/core/reportsApi'
import {
  periodicReportRunPath,
  periodicReportsPath,
} from '../../modules/business-services/core/periodicReportsPath'

const RECENT_RUNS = 4

/** One run in «آخرین گزارش‌ها»: a run of the newest schedule, or another schedule's last run. */
export type RecentRun = {
  token: string
  title: string
  at: string
  atLabel: string
  trigger?: string
  state?: string | null
  smsTitle?: string | null
}

const fa = (value: number) => value.toLocaleString('fa-IR')

/** The schedule that ran last, whose own runs the card reads. */
export const newestSchedule = (schedules: ReportSchedule[]): ReportSchedule | undefined =>
  schedules
    .filter((schedule) => schedule.lastRun?.at)
    .sort((a, b) => (b.lastRun?.at || '').localeCompare(a.lastRun?.at || ''))[0]

/**
 * The newest runs across the business's schedules: the newest schedule's own runs, and every other schedule's
 * last run, newest first.
 */
export const recentRuns = (
  schedules: ReportSchedule[],
  newest: ReportSchedule | undefined,
  newestRuns: ReportRunRow[]
): RecentRun[] => {
  const rows: RecentRun[] = newestRuns.map((run) => ({
    token: run.token,
    title: newest?.title || '',
    at: run.createdAt || '',
    atLabel: run.createdAtLabel || '',
    trigger: run.trigger,
    state: run.state,
    smsTitle: run.sms && run.sms.state !== 'none' ? run.sms.title : undefined,
  }))
  schedules.forEach((schedule) => {
    const last = schedule.lastRun
    if (!last || rows.some((row) => row.token === last.token)) return
    rows.push({
      token: last.token,
      title: schedule.title,
      at: last.at || '',
      atLabel: last.atLabel || '',
      state: last.state,
      smsTitle: lastRunSmsTitle(last),
    })
  })
  return rows.sort((a, b) => b.at.localeCompare(a.at)).slice(0, RECENT_RUNS)
}

const Card: FC<{eyebrow: string; title: string; icon: string; children: ReactNode}> = ({
  eyebrow,
  title,
  icon,
  children,
}) => (
  <section className='mp-panel mp-panel--reports' aria-labelledby='mp-reports-title'>
    <div className='mp-panel__head'>
      <div>
        <span>{eyebrow}</span>
        <h2 id='mp-reports-title'>{title}</h2>
      </div>
      <i className={`las ${icon}`} aria-hidden='true' />
    </div>
    {children}
  </section>
)

const ReportsLink: FC<{primary?: boolean}> = ({primary}) => (
  <Link className={clsx('btn btn-sm', primary ? 'btn-primary' : 'btn-light-primary')} to='/reports'>
    <i className='las la-chart-pie' aria-hidden='true' />
    گزارش صورتحساب‌ها
  </Link>
)

/** Without one business (an administrator on «همه کسب‌وکارها»), on premises, or while the service is out. */
const PlainCard: FC<{withPeriodic: boolean}> = ({withPeriodic}) => (
  <Card eyebrow='گزارش‌ها' title='گزارش صورتحساب‌ها' icon='la-chart-pie'>
    <p className='mp-reports__text'>
      فروش و وضعیت ثبت صورتحساب‌ها در سامانهٔ مؤدیان، به تفکیک فصل، ماه یا بازهٔ دلخواه، با نمودار و
      خروجی اکسل.
    </p>
    <div className='mp-reports__actions'>
      <ReportsLink primary />
      {withPeriodic && (
        <Link className='btn btn-sm btn-light-primary' to={periodicReportsPath(0)}>
          <i className='las la-calendar-check' aria-hidden='true' />
          گزارش‌های دوره‌ای
        </Link>
      )}
    </div>
  </Card>
)

const PROMO_POINTS = [
  {icon: 'la-clock', text: 'از هر ۶ ساعت تا هر ۳۰ روز، در ساعتی که خودتان می‌خواهید'},
  {icon: 'la-filter', text: 'با همان فیلترهای فهرست صورتحساب‌ها'},
  {icon: 'la-sms', text: 'خلاصه با پیامک برای شما و همکارانتان'},
  {icon: 'la-chart-bar', text: 'گزارش کامل با نمودار، چاپ و خروجی اکسل در پنل'},
]

/** No report yet: what a periodic report is, a sample SMS, and the way to make the first one. */
const PromoCard: FC<{companyId: number; canChange: boolean}> = ({companyId, canChange}) => (
  <Card eyebrow='گزارش‌ها' title='گزارش دوره‌ای پیامکی' icon='la-calendar-check'>
    <div className='mp-reports__promo'>
      <div className='mp-reports__pitch'>
        <span className='mp-reports__badge'>
          <i className='las la-star' aria-hidden='true' />
          تازه
        </span>
        <p className='mp-reports__lead'>
          وضعیت درج و ارسال صورتحساب‌ها را بی‌آنکه وارد پنل شوید، سر ساعت روی گوشی‌تان ببینید.
        </p>
        <ul className='mp-reports__points'>
          {PROMO_POINTS.map((point) => (
            <li key={point.icon}>
              <i className={`las ${point.icon}`} aria-hidden='true' />
              {point.text}
            </li>
          ))}
        </ul>
        <div className='mp-reports__actions'>
          <Link
            className='btn btn-sm btn-primary'
            to={periodicReportsPath(companyId, {newSchedule: canChange})}
          >
            <i className='las la-plus' aria-hidden='true' />
            {canChange ? 'ساخت نخستین گزارش' : 'آشنایی با گزارش‌های دوره‌ای'}
          </Link>
          <ReportsLink />
        </div>
      </div>
      <figure className='mp-reports__sample' aria-label='نمونهٔ پیامک گزارش روزانه'>
        <div className='mp-reports__bubble'>
          <strong>معتمد آسا | گزارش روزانه</strong>
          <span>درج‌شده: ۱۲۰</span>
          <span>ثبت‌شده در سامانه: ۱۱۰</span>
          <span>رد سازمان: ۳</span>
          <span>
            گزارش کامل:{' '}
            <bdi dir='ltr' className='mp-reports__bubble-link'>
              my.asatsp.ir/br/…
            </bdi>
          </span>
        </div>
        <figcaption>نمونهٔ پیامک</figcaption>
      </figure>
    </div>
  </Card>
)

const STATE_TONES: Record<string, string> = {completed: 'ok', running: 'busy', failed: 'bad', unpaid: 'warn'}

/** The business's reports: the newest run's figures, the latest runs and when the next one is due. */
const HistoryCard: FC<{companyId: number; schedules: ReportSchedule[]}> = ({companyId, schedules}) => {
  const newest = newestSchedule(schedules)
  const runs = useQuery(
    [...reportKeys.runs(companyId, newest?.id || 0), 'dashboard'],
    () => getReportRuns(companyId, newest?.id || 0, 1, RECENT_RUNS),
    {enabled: Boolean(newest), retry: false, refetchOnWindowFocus: false, staleTime: 60_000}
  )
  const latest = runs.data?.items[0]
  const rows = recentRuns(schedules, newest, runs.data?.items || [])
  const enabled = schedules.filter((schedule) => schedule.enabled)
  const next = enabled
    .filter((schedule) => schedule.nextRunAt)
    .sort((a, b) => (a.nextRunAt || '').localeCompare(b.nextRunAt || ''))[0]

  return (
    <Card eyebrow='گزارش‌ها' title='گزارش‌های دوره‌ای' icon='la-calendar-check'>
      <div className='mp-reports__facts'>
        <span>
          <strong>{fa(schedules.length)}</strong> گزارش
          {enabled.length < schedules.length && ` (${fa(enabled.length)} فعال)`}
        </span>
        {next?.nextRunAtLabel ? (
          <span>
            اجرای بعدی: <bdi>{next.nextRunAtLabel}</bdi> · {next.title}
          </span>
        ) : (
          <span>هیچ گزارشی فعال نیست</span>
        )}
      </div>

      <div className='mp-reports__history'>
        {latest?.headline && newest && (
          <div className='mp-reports__latest'>
            <div className='mp-reports__latest-head'>
              آخرین گزارش: <strong>{newest.title}</strong>
              <bdi>{latest.periodLabel}</bdi>
            </div>
            <dl className='mp-reports__figures'>
              <div>
                <dt>درج‌شده</dt>
                <dd>{fa(latest.headline.created)}</dd>
              </div>
              <div>
                <dt>ارسال‌شده</dt>
                <dd>{fa(latest.headline.sent)}</dd>
              </div>
              <div>
                <dt>ثبت‌شده</dt>
                <dd className='is-ok'>{fa(latest.headline.registered)}</dd>
              </div>
              <div>
                <dt>رد سازمان</dt>
                <dd className={latest.headline.rejected > 0 ? 'is-bad' : undefined}>
                  {fa(latest.headline.rejected)}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {rows.length > 0 ? (
          <ul className='mp-reports__runs' aria-label='آخرین گزارش‌های ساخته‌شده'>
            {rows.map((row) => (
              <li key={row.token}>
                <Link to={periodicReportRunPath(row.token)} className='mp-reports__run'>
                  <span
                    className={clsx('mp-reports__dot', `is-${STATE_TONES[row.state || ''] || 'ok'}`)}
                    aria-hidden='true'
                  />
                  <span className='mp-reports__run-text'>
                    <strong>{row.title}</strong>
                    <small>
                      <bdi>{row.atLabel}</bdi>
                      {row.trigger ? ` · ${triggerTitle(row.trigger)}` : ''}
                      {row.smsTitle ? ` · پیامک: ${row.smsTitle}` : ''}
                    </small>
                  </span>
                  <i className='las la-angle-left' aria-hidden='true' />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className='mp-reports__text mb-0'>
            هنوز گزارشی ساخته نشده است؛ نخستین گزارش در زمان اجرای بعدی ساخته می‌شود.
          </p>
        )}
      </div>

      <div className='mp-reports__actions'>
        <Link className='btn btn-sm btn-primary' to={periodicReportsPath(companyId)}>
          <i className='las la-calendar-check' aria-hidden='true' />
          همهٔ گزارش‌های دوره‌ای
        </Link>
        <ReportsLink />
      </div>
    </Card>
  )
}

/**
 * «گزارش‌ها» on the dashboard. With no periodic report yet it introduces the service; with reports it shows the
 * newest run's figures and the latest runs. Every state leads to «گزارش‌ها». It reads the same periodic-report
 * list as «گزارش‌ها ← گزارش‌های دوره‌ای», under the same key.
 */
export const DashboardReports: FC<{companyId: number}> = ({companyId}) => {
  const {currentUser} = useAuth()
  const onPremises = isOnPremisesInstallation(currentUser)
  const periodic = companyId > 0 && !onPremises
  const settings = useQuery(reportKeys.settings(companyId), () => getReportSettings(companyId), {
    enabled: periodic,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })

  if (!periodic) return <PlainCard withPeriodic={!onPremises} />
  if (settings.isLoading) {
    return (
      <Card eyebrow='گزارش‌ها' title='گزارش‌های دوره‌ای' icon='la-calendar-check'>
        <div className='mp-reports__loading' role='status'>
          <span className='spinner-border spinner-border-sm' aria-hidden='true' />
          در حال دریافت گزارش‌ها…
        </div>
      </Card>
    )
  }
  // A failed read or a service that is not open yet leaves the plain way into «گزارش‌ها».
  if (!settings.data?.available) return <PlainCard withPeriodic={false} />

  const {schedules, canChange} = settings.data
  return schedules.length === 0 ? (
    <PromoCard companyId={companyId} canChange={canChange} />
  ) : (
    <HistoryCard companyId={companyId} schedules={schedules} />
  )
}
